import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { HANDLE_CHECKOUT_SESSION_COMPLETED } from '@shared/graphql/mutations/payment';
import { useAffiliateTracking } from '../hooks/useAffiliateTracking';
import googleAnalytics from '../services/googleAnalytics';
import MetaPixelService from '../services/metaPixel';

export const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(true);
  const [hasBackendError, setHasBackendError] = useState<boolean>(false);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [hasProcessedSession, setHasProcessedSession] = useState<boolean>(false);
  const { trackConversion } = useAffiliateTracking();

  // For local development only - in production, webhooks handle this
  // Includes localhost, 127.0.0.1, and any subdomain of localhost (e.g., api.localhost)
  const isLocalDevelopment = window.location.hostname === 'localhost' ||
                            window.location.hostname === '127.0.0.1' ||
                            window.location.hostname.endsWith('.localhost');
  
  const [handleCheckoutCompleted] = useMutation(HANDLE_CHECKOUT_SESSION_COMPLETED, {
    onCompleted: async (data) => {
      const timestamp = new Date().toISOString();
      console.log(`✅ [FRONTEND] ${timestamp} Checkout session processed successfully:`, data);
      setSubscriptionData(data.handleCheckoutSessionCompleted);
      setIsProcessing(false);
      
      // Track affiliate conversion after successful checkout session processing
      if (data.handleCheckoutSessionCompleted?.success && data.handleCheckoutSessionCompleted?.paymentIntentId) {
        try {
          await trackConversion(data.handleCheckoutSessionCompleted.paymentIntentId);
          console.log(`✅ Affiliate conversion tracked for payment: ${data.handleCheckoutSessionCompleted.paymentIntentId}`);
        } catch (conversionError) {
          // Don't fail the success flow if affiliate tracking fails
          console.warn('⚠️ Affiliate conversion tracking failed:', conversionError);
        }
      }
      
      if (!data.handleCheckoutSessionCompleted?.success) {
        console.warn('⚠️ Backend processing had issues, but payment was successful');
        setHasBackendError(true);
      }
    },
    onError: (error) => {
      const timestamp = new Date().toISOString();
      console.error(`❌ [FRONTEND] ${timestamp} Error processing checkout session:`, error);
      setHasBackendError(true);
      setIsProcessing(false);
    }
  });

  useEffect(() => {
    const timestamp = new Date().toISOString();
    const effectId = Math.random().toString(36).substring(7);
    
    console.log(`🚀 [FRONTEND-${effectId}] ${timestamp} useEffect triggered`);
    console.log(`🔍 [FRONTEND-${effectId}] Effect state:`, {
      hasProcessedSession,
      isLocalDevelopment,
      searchParamsKeys: Array.from(searchParams.keys()),
      sessionIdParam: searchParams.get('session_id')
    });
    
    const sessionIdParam = searchParams.get('session_id');
    if (sessionIdParam && !hasProcessedSession) {
      console.log(`✅ [FRONTEND-${effectId}] Processing session for first time:`, sessionIdParam);
      setSessionId(sessionIdParam);
      setHasProcessedSession(true); // Mark as processed to prevent duplicate calls

      // GA and Meta purchase conversions - value comes from the begin_checkout stash,
      // deduplicated per session_id so refreshes don't double count
      const purchase = googleAnalytics.trackPurchase(sessionIdParam);
      if (purchase) MetaPixelService.trackPurchase(purchase.value, purchase.currency, sessionIdParam);
      
      if (isLocalDevelopment) {
        // For local development, process the checkout session directly
        // since Stripe webhooks can't reach localhost
        console.log(`🔧 [FRONTEND-${effectId}] Local development detected - processing checkout session directly`);
        console.log(`🔍 [FRONTEND-${effectId}] Session ID: ${sessionIdParam}`);
        console.log(`🔍 [FRONTEND-${effectId}] Processing checkout session for the first and only time`);
        console.log(`📡 [FRONTEND-${effectId}] Calling GraphQL mutation...`);
        
        handleCheckoutCompleted({
          variables: {
            sessionId: sessionIdParam
          }
        });
      } else {
        // In production, webhooks handle this
        console.log(`✅ [FRONTEND-${effectId}] Payment successful! Stripe webhook will handle subscription creation.`);
        
        // Try to track affiliate conversion with session ID if available
        const paymentIntentParam = searchParams.get('payment_intent') || searchParams.get('pi');
        if (paymentIntentParam) {
          try {
            trackConversion(paymentIntentParam);
            console.log(`✅ Production mode: Affiliate conversion tracked for payment: ${paymentIntentParam}`);
          } catch (conversionError) {
            console.warn('⚠️ Production mode: Affiliate conversion tracking failed:', conversionError);
          }
        }
        
        // Just mark as processed after a short delay to show success
        setTimeout(() => {
          console.log(`⏱️ [FRONTEND-${effectId}] Production timeout complete - marking as processed`);
          setIsProcessing(false);
          setSubscriptionData({
            success: true,
            message: 'Payment processed successfully via Stripe webhook'
          });
        }, 2000); // 2 second delay to show processing animation
      }
    } else if (sessionIdParam && hasProcessedSession) {
      console.log(`⚠️ [FRONTEND-${effectId}] Session already processed - skipping duplicate call`);
    } else if (!sessionIdParam) {
      console.log(`❌ [FRONTEND-${effectId}] No session ID found in URL parameters`);
      setHasBackendError(true);
      setIsProcessing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only re-run when searchParams change

  const handleContinue = () => {
    // Check if we have track information in URL parameters to redirect back to original track
    const returnUrl = searchParams.get('return_url');
    const trackState = searchParams.get('track_state');
    const trackName = searchParams.get('track_name');
    const date = searchParams.get('date');
    
    // If we have track information, redirect to the specific track tips page
    if (trackState && trackName && date) {
      navigate(`/${trackState}/${trackName}/${date}`);
      return;
    }
    
    if (returnUrl) {
      // If we have a return URL, use that
      navigate(returnUrl);
      return;
    }
    
    // Try to get track information from sessionStorage (set when user starts payment flow)
    try {
      const storedTrackInfo = sessionStorage.getItem('payment_return_track');
      if (storedTrackInfo) {
        const trackInfo = JSON.parse(storedTrackInfo);
        if (trackInfo.state && trackInfo.track && trackInfo.date) {
          // Clear the stored info after use
          sessionStorage.removeItem('payment_return_track');
          navigate(`/${trackInfo.state}/${trackInfo.track}/${trackInfo.date}`);
          return;
        }
      }
    } catch (error) {
      console.warn('Could not retrieve track info from sessionStorage:', error);
    }
    
    // Fallback to home page
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Mobile Layout */}
      <div className="md:hidden min-h-screen flex flex-col">
        <div className="flex-1 p-4 pt-8">
          <div className="bg-white rounded-xl shadow-xl p-6 text-center">
            {/* Mobile Processing State */}
            {isProcessing && (
              <>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-3">
                  Finalizing Your Subscription
                </h1>
                <p className="text-gray-600 mb-4 text-base">
                  Please wait while we activate your TippingIQ racing tips subscription...
                </p>
              </>
            )}

            {/* Mobile Success State */}
            {!isProcessing && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-3">
                  🎉 Welcome to TippingIQ!
                </h1>
                <p className="text-gray-700 mb-4 text-base">
                  Your payment was successful and your subscription is now active!
                </p>
                
                {/* Mobile Condensed Success Details */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 mb-4 text-left">
                  <h3 className="text-base font-semibold text-gray-800 mb-2">What's Next?</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center text-gray-700">
                      <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Premium racing tips & analysis
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Daily race selections
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Expert track insights
                    </div>
                  </div>
                </div>

                {/* Mobile Subscription Details */}
                {subscriptionData?.success && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 text-left">
                    <p className="text-xs font-medium text-gray-700 mb-1">Subscription Details:</p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>✓ {subscriptionData.subscriptionIds?.length || 1} package(s) activated</p>
                      {subscriptionData.nextBillingDate && (
                        <p>✓ Next billing: {new Date(subscriptionData.nextBillingDate).toLocaleDateString('en-AU', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          timeZone: 'Australia/Brisbane'
                        })}</p>
                      )}
                      {!subscriptionData.nextBillingDate && subscriptionData.accessExpiresAt && (
                        <p>✓ Access until: {new Date(subscriptionData.accessExpiresAt).toLocaleDateString('en-AU', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          timeZone: 'Australia/Brisbane'
                        })}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Mobile Backend Error Warning */}
                {hasBackendError && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <div className="flex items-start">
                      <svg className="w-4 h-4 text-yellow-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <p className="text-xs text-yellow-800">
                        Your subscription may take a few minutes to fully activate. You'll receive a confirmation email once complete.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Mobile Sticky Action Button */}
        {!isProcessing && (
          <div className="bg-white border-t border-gray-200 p-4">
            <button
              onClick={handleContinue}
              className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-4 px-6 rounded-xl hover:from-blue-700 hover:to-green-700 transition-all duration-200 font-semibold text-lg shadow-lg"
            >
              Start Exploring Tips →
            </button>
            <div className="mt-3 space-y-1 text-center">
              <p className="text-xs text-gray-500">
                📧 Confirmation email will arrive shortly
              </p>
              <p className="text-xs text-gray-400">
                Need help? Contact our support team anytime
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex items-center justify-center min-h-screen p-4">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-xl p-8 text-center">
        {/* Processing State */}
        {isProcessing && (
          <>
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Finalizing Your Subscription
            </h1>
            <p className="text-gray-600 mb-6 text-lg">
              Please wait while we activate your TippingIQ racing tips subscription...
            </p>
          </>
        )}

        {/* Success State - Always show success since payment completed */}
        {!isProcessing && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              🎉 Welcome to TippingIQ!
            </h1>
            <p className="text-gray-700 mb-6 text-lg">
              Your payment was successful and your subscription is now active. Get ready to start winning with our expert racing tips!
            </p>
            
            {/* Success Details */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 mb-6 text-left">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">What's Next?</h3>
              <div className="space-y-2">
                <div className="flex items-center text-gray-700">
                  <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Access to premium racing tips and analysis
                </div>
                <div className="flex items-center text-gray-700">
                  <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Daily race selections and betting strategies
                </div>
                <div className="flex items-center text-gray-700">
                  <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Expert insights and track analysis
                </div>
              </div>
            </div>

            {/* Subscription Details - Only show if we have successful data */}
            {subscriptionData?.success && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-medium text-gray-700 mb-2">Subscription Details:</p>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>✓ {subscriptionData.subscriptionIds?.length || 1} package(s) activated</p>
                  {subscriptionData.nextBillingDate && (
                    <p>✓ Next billing: {new Date(subscriptionData.nextBillingDate).toLocaleDateString('en-AU', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      timeZone: 'Australia/Brisbane'
                    })}</p>
                  )}
                  {!subscriptionData.nextBillingDate && subscriptionData.accessExpiresAt && (
                    <p>✓ Access until: {new Date(subscriptionData.accessExpiresAt).toLocaleDateString('en-AU', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      timeZone: 'Australia/Brisbane'
                    })}</p>
                  )}
                </div>
              </div>
            )}

            {/* Backend Processing Warning - Only show if there were issues */}
            {hasBackendError && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-sm text-yellow-800">
                    Your subscription may take a few minutes to fully activate. You'll receive a confirmation email once complete.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Continue Button */}
        {!isProcessing && (
          <button
            onClick={handleContinue}
            className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-4 px-6 rounded-xl hover:from-blue-700 hover:to-green-700 transition-all duration-200 font-semibold text-lg shadow-lg"
          >
            Start Exploring Tips →
          </button>
        )}

        {/* Additional Info */}
        {!isProcessing && (
          <div className="mt-6 space-y-2">
            <p className="text-sm text-gray-500">
              📧 Confirmation email will arrive shortly
            </p>
            <p className="text-xs text-gray-400">
              Need help? Contact our support team anytime
            </p>
          </div>
        )}

        {/* Debug Info - Only in development */}
        {process.env.NODE_ENV === 'development' && sessionId && (
          <div className="mt-6 p-3 bg-gray-100 rounded-lg text-left">
            <p className="text-xs font-semibold text-gray-600 mb-1">Debug Info (Dev Only):</p>
            <p className="text-xs font-mono text-gray-500 break-all">Session: {sessionId}</p>
            <p className="text-xs text-gray-500 mt-1">
              {isLocalDevelopment ? '🔧 Local mode: Direct processing' : '🌐 Production mode: Webhook processing'}
            </p>
            {subscriptionData && (
              <details className="mt-2">
                <summary className="text-xs text-gray-600 cursor-pointer">Backend Response</summary>
                <pre className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">
                  {JSON.stringify(subscriptionData, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};